import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import { ArrowLeft, Flag, MapPin, Navigation, PlayCircle, StopCircle, WifiOff, AlertTriangle, FileText } from 'lucide-react';
import HRHeader, { getHRUser } from '@/components/HRHeader';
import {
  authHeaders, coords, escapeHtml, formatMinutes, formatTime, numberedIcon, todayIST, useLeafletMap,
} from '@/lib/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#db2777'];
const END_REASONS = {
  employee: 'by employee',
  max_duration: 'automatically (maximum duty hours reached)',
  logout: 'at logout',
  auto_closed: 'by server (phone stopped responding)',
  superseded: 'by server (a new duty was started)',
};
const GAP_REASONS = {
  no_location: 'Location data unavailable — tracking off, no GPS signal, or phone switched off',
  no_location_after_start: 'No location recorded after Start Duty',
  no_location_before_end: 'No location recorded before End Duty',
};

export default function HRTrackingHistory() {
  const navigate = useNavigate();
  const [user] = useState(getHRUser);
  const [params, setParams] = useSearchParams();
  const employeeId = params.get('employee') || '';
  const date = params.get('date') || todayIST();
  const [employees, setEmployees] = useState([]);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPoints, setShowPoints] = useState(false);
  const [mapRef, map] = useLeafletMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/hr/login');
      return;
    }
    axios.get(`${API}/employees`, { headers: authHeaders() })
      .then((r) => setEmployees(r.data.filter((e) => e.field_tracking || e.id === employeeId).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setError('Could not load employees.'));
  }, [user, navigate, employeeId]);

  useEffect(() => {
    if (!employeeId || !date) {
      setDay(null);
      return;
    }
    setLoading(true);
    setError(null);
    axios.get(`${API}/tracking/day`, { params: { employee_id: employeeId, date }, headers: authHeaders() })
      .then((r) => setDay(r.data))
      .catch((e) => {
        setDay(null);
        if (e.response?.status === 401) navigate('/hr/login');
        else setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not load location history.');
      })
      .finally(() => setLoading(false));
  }, [employeeId, date, navigate]);

  // Visits and unknown-place stops get numbers in time order, shared by map and timeline
  const places = useMemo(() => (day?.segments || []).filter((s) => s.type === 'visit' || s.type === 'stop'), [day]);

  const timeline = useMemo(() => {
    if (!day) return [];
    const items = [...day.segments, ...(day.offline_periods || [])];
    const rank = { duty_start: 0, offline: 2, duty_end: 3 };
    return items.sort((a, b) => new Date(a.start) - new Date(b.start) || (rank[a.type] ?? 1) - (rank[b.type] ?? 1));
  }, [day]);

  useEffect(() => {
    if (!map) return;
    if (!layerRef.current) layerRef.current = L.layerGroup().addTo(map);
    const layer = layerRef.current;
    layer.clearLayers();
    if (!day) return;

    day.locations.forEach((loc) => {
      L.circle([loc.latitude, loc.longitude], {
        radius: loc.radius || 100,
        color: loc.assigned ? '#18181b' : '#a1a1aa',
        weight: loc.assigned ? 2 : 1,
        dashArray: loc.assigned ? null : '4 4',
        fillOpacity: loc.assigned ? 0.08 : 0.03,
      }).bindTooltip(`${escapeHtml(loc.name)}${loc.assigned ? ' (assigned)' : ''}`).addTo(layer);
    });
    const bounds = [];
    day.sessions.forEach((session, i) => {
      const pts = day.points.filter((p) => p.session_id === session.id);
      if (!pts.length) return;
      const color = SESSION_COLORS[i % SESSION_COLORS.length];
      // Draw the route only between points recorded close together: a tracking gap is not a known path
      let piece = [pts[0]];
      const gapMs = day.gap_minutes * 60000;
      const flush = () => {
        if (piece.length > 1) L.polyline(piece.map((p) => [p.lat, p.lng]), { color, weight: 4, opacity: 0.8 }).addTo(layer);
      };
      pts.slice(1).forEach((p) => {
        if (new Date(p.t) - new Date(piece[piece.length - 1].t) > gapMs) {
          flush();
          L.polyline([[piece[piece.length - 1].lat, piece[piece.length - 1].lng], [p.lat, p.lng]], { color: '#a1a1aa', weight: 2, dashArray: '6 6' })
            .bindTooltip('Tracking gap — route unknown').addTo(layer);
          piece = [];
        }
        piece.push(p);
      });
      flush();
      pts.forEach((p) => {
        L.circleMarker([p.lat, p.lng], { radius: 3, color: p.offline ? '#d97706' : color, weight: 1, fillOpacity: 0.9 })
          .bindTooltip(`${formatTime(p.t)}${p.accuracy != null ? ` · ±${Math.round(p.accuracy)} m` : ''}${p.offline ? ' · uploaded later' : ''}`)
          .addTo(layer);
      });
      L.marker([pts[0].lat, pts[0].lng], { icon: numberedIcon('S', '#16a34a', true) }).bindTooltip(`Start ${formatTime(session.started_at)}`).addTo(layer);
      const last = pts[pts.length - 1];
      if (session.ended_at) {
        L.marker([last.lat, last.lng], { icon: numberedIcon('E', '#dc2626', true) }).bindTooltip(`End ${formatTime(session.ended_at)}`).addTo(layer);
      } else {
        L.circleMarker([last.lat, last.lng], { radius: 10, color: '#fff', weight: 3, fillColor: '#16a34a', fillOpacity: 1 })
          .bindTooltip(`Latest location ${formatTime(last.t)}`, { permanent: true, direction: 'top', offset: [0, -10] }).addTo(layer);
      }
      bounds.push(...pts.map((p) => [p.lat, p.lng]));
    });
    places.forEach((place, i) => {
      const isVisit = place.type === 'visit';
      L.marker([place.latitude, place.longitude], { icon: numberedIcon(String(i + 1), isVisit ? '#18181b' : '#d97706'), zIndexOffset: 1000 })
        .bindPopup(`
          <div style="min-width:190px">
            <div style="font-weight:700">${i + 1}. ${escapeHtml(isVisit ? place.location_name : 'Unknown place')}</div>
            <div style="color:#71717a">${escapeHtml(day.employee.name)}</div>
            <div style="margin-top:6px">Arrival: <b>${place.arrival_exact === false ? 'before ' : ''}${formatTime(place.start)}</b></div>
            <div>Departure: <b>${place.departure_exact === false ? 'after ' : ''}${formatTime(place.end)}</b></div>
            <div>Duration: <b>${formatMinutes(place.duration_min)}</b>${place.arrival_exact === false || place.departure_exact === false ? ' (at least)' : ''}</div>
            ${isVisit && !place.assigned ? '<div style="color:#b45309;margin-top:4px">Not an assigned site</div>' : ''}
          </div>`)
        .addTo(layer);
      bounds.push([place.latitude, place.longitude]);
    });
    if (bounds.length) map.fitBounds(L.latLngBounds(bounds).pad(0.2), { maxZoom: 16 });
  }, [map, day, places]);

  const focusOn = (item) => {
    if (!map || !day) return;
    if (item.latitude != null) {
      map.setView([item.latitude, item.longitude], 16);
      return;
    }
    const pts = day.points.filter((p) => new Date(p.t) >= new Date(item.start) && new Date(p.t) <= new Date(item.end));
    if (pts.length) map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])).pad(0.3), { maxZoom: 16 });
  };

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    if (!next.get('date')) next.set('date', todayIST());
    setParams(next);
  };

  if (!user) return null;
  const summary = day?.summary;

  return (
    <div className="min-h-screen bg-zinc-50">
      <HRHeader title="Location History" user={user} active="/hr/tracking" />
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-end gap-4">
          <button onClick={() => navigate('/hr/tracking')} className="h-10 px-3 flex items-center gap-1 text-sm font-bold uppercase text-zinc-700 hover:text-zinc-900">
            <ArrowLeft size={16} /> Live tracking
          </button>
          <label className="text-sm">
            <span className="block text-zinc-500 font-medium mb-1 uppercase tracking-wider text-xs">Field employee</span>
            <select value={employeeId} onChange={(e) => update('employee', e.target.value)} className="h-10 px-3 border-2 border-zinc-200 rounded-sm min-w-[240px] bg-white" data-testid="history-employee">
              <option value="">Select employee…</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{`${e.name} (${e.employee_id})`}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-zinc-500 font-medium mb-1 uppercase tracking-wider text-xs">Date</span>
            <input type="date" value={date} max={todayIST()} onChange={(e) => update('date', e.target.value)} className="h-10 px-3 border-2 border-zinc-200 rounded-sm bg-white" data-testid="history-date" />
          </label>
          <button
            onClick={() => navigate(`/hr/tracking/reports?date=${date}${employeeId ? `&employee=${employeeId}` : ''}`)}
            className="h-10 px-3 flex items-center gap-1 text-sm font-bold uppercase text-zinc-700 hover:text-zinc-900"
          >
            <FileText size={16} /> Reports
          </button>
        </div>

        {error && <div className="bg-red-50 border-2 border-red-200 text-red-800 rounded-lg p-3 text-sm font-medium">{error}</div>}
        {summary?.insufficient_data && (
          <div className="bg-amber-50 border-2 border-amber-200 text-amber-900 rounded-lg p-3 text-sm font-medium" data-testid="insufficient-data">
            Insufficient location data: too few usable locations were recorded to work out visits and travel for this duty.
          </div>
        )}

        {summary && summary.sessions > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="history-summary">
            <Stat label="Total duty" value={formatMinutes(summary.duty_min)} />
            <Stat label="Site visits" value={`${summary.visits} · ${formatMinutes(summary.visit_min)}`} />
            <Stat label="Sites visited" value={summary.sites_visited} />
            <Stat label="Travel" value={`${formatMinutes(summary.travel_min)} · ${summary.distance_km} km`} />
            <Stat label="Other stops" value={formatMinutes(summary.unknown_stop_min)} warn={summary.flagged_stops > 0} />
            <Stat label="Tracking gaps" value={summary.gaps ? `${summary.gaps} · ${formatMinutes(summary.gap_min)}` : 'None'} warn={summary.gaps > 0} />
            <Stat label="Locations recorded" value={summary.point_count} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white border-2 border-zinc-100 rounded-lg overflow-hidden">
            <div ref={mapRef} style={{ height: 600 }} data-testid="history-map" />
          </div>
          <div className="lg:col-span-2 bg-white border-2 border-zinc-100 rounded-lg p-5 overflow-y-auto" style={{ maxHeight: 604 }} data-testid="history-timeline">
            {!employeeId && <p className="text-zinc-500 text-center py-12">Select an employee and a date.</p>}
            {loading && <p className="text-zinc-500 text-center py-12">Loading…</p>}
            {day && !loading && day.sessions.length === 0 && (
              <p className="text-zinc-500 text-center py-12">{day.employee.name} did not start field duty on this day.</p>
            )}
            {day && !loading && day.sessions.length > 0 && (
              <ol className="space-y-1">
                {timeline.map((item, idx) => (
                  <TimelineItem key={idx} item={item} number={places.indexOf(item) + 1} onClick={() => focusOn(item)} />
                ))}
              </ol>
            )}
          </div>
        </div>

        {day && day.points.length > 0 && (
          <div className="bg-white border-2 border-zinc-100 rounded-lg">
            <button onClick={() => setShowPoints(!showPoints)} className="w-full text-left p-4 font-bold uppercase tracking-wider text-sm text-zinc-900">
              {showPoints ? 'Hide' : 'Show'} recorded locations ({summary.point_count}{day.points.length < summary.point_count ? `, map shows ${day.points.length}` : ''})
            </button>
            {showPoints && (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr>{['Time', 'Latitude', 'Longitude', 'Accuracy', ''].map((h) => <th key={h} className="text-left px-4 py-2 text-xs font-bold text-zinc-600 uppercase">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {day.points.map((p, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="px-4 py-1.5">{formatTime(p.t)}</td>
                        <td className="px-4 py-1.5 font-mono">{p.lat.toFixed(6)}</td>
                        <td className="px-4 py-1.5 font-mono">{p.lng.toFixed(6)}</td>
                        <td className="px-4 py-1.5">{p.accuracy != null ? `±${Math.round(p.accuracy)} m` : '—'}</td>
                        <td className="px-4 py-1.5 text-xs text-zinc-500">{[p.offline && 'uploaded later', p.is_mock && 'mock location'].filter(Boolean).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, warn }) => (
  <div className="bg-white border-2 border-zinc-100 rounded-lg p-3">
    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`text-lg font-extrabold ${warn ? 'text-amber-700' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

function TimelineItem({ item, number, onClick }) {
  let icon;
  let time = formatTime(item.start);
  let lines = [];
  let tone = 'text-zinc-900';
  switch (item.type) {
    case 'duty_start':
      icon = <PlayCircle size={18} className="text-green-600" />;
      lines = [<b key="t">START DUTY</b>, item.source === 'app_offline' && 'Started while the phone was offline'];
      break;
    case 'duty_end':
      icon = <StopCircle size={18} className="text-red-600" />;
      lines = [<b key="t">END DUTY</b>, item.source && item.source !== 'employee' && `Ended ${END_REASONS[item.source] || item.source}`];
      break;
    case 'visit':
      icon = <MapPin size={18} />;
      time = `${formatTime(item.start)} – ${formatTime(item.end)}`;
      lines = [
        <b key="t">{number}. {item.location_name}{!item.assigned && <span className="ml-1 text-xs font-medium text-amber-700">(not assigned)</span>}</b>,
        `${item.arrival_exact ? 'Arrived' : 'Already there at'} ${formatTime(item.start)} · ${item.departure_exact ? 'Left' : 'Last seen'} ${formatTime(item.end)}`,
        `Stayed ${item.arrival_exact && item.departure_exact ? '' : 'at least '}${formatMinutes(item.duration_min)}`,
      ];
      break;
    case 'stop':
      icon = <Flag size={18} className="text-amber-600" />;
      time = `${formatTime(item.start)} – ${formatTime(item.end)}`;
      lines = [<b key="t">{number}. Stop at unknown place · {formatMinutes(item.duration_min)}</b>,
        `${coords(item.latitude, item.longitude)}${item.flagged ? ' · long stop away from approved sites' : ''}`];
      tone = 'text-amber-800';
      break;
    case 'travel':
      icon = <Navigation size={18} className="text-blue-600" />;
      time = `${formatTime(item.start)} – ${formatTime(item.end)}`;
      lines = [<b key="t">TRAVEL {item.from_label} → {item.to_label}</b>, `${formatMinutes(item.duration_min)} · ${(item.distance_m / 1000).toFixed(1)} km`];
      break;
    case 'gap':
      icon = <AlertTriangle size={18} className="text-amber-600" />;
      time = `${formatTime(item.start)} – ${formatTime(item.end)}`;
      lines = [<b key="t">TRACKING GAP · {formatMinutes(item.duration_min)}</b>,
        GAP_REASONS[item.reason] || GAP_REASONS.no_location,
        item.moved_m >= 100 && `Position changed by ${(item.moved_m / 1000).toFixed(1)} km — route unknown`];
      tone = 'text-amber-800';
      break;
    case 'offline':
      icon = <WifiOff size={18} className="text-amber-600" />;
      time = `${formatTime(item.start)} – ${formatTime(item.end)}`;
      lines = [<b key="t">Phone offline</b>, `${item.points} locations recorded without internet and uploaded later`];
      tone = 'text-amber-800';
      break;
    default:
      return null;
  }
  return (
    <li className="flex gap-3 p-2 rounded hover:bg-zinc-50 cursor-pointer" onClick={onClick} data-testid={`timeline-${item.type}`}>
      <div className="pt-0.5">{icon}</div>
      <div className={tone}>
        <p className="text-xs font-mono text-zinc-500">{time}</p>
        {lines.filter(Boolean).map((line, i) => <p key={i} className={i ? 'text-xs text-zinc-600' : ''}>{line}</p>)}
      </div>
    </li>
  );
}
